import { Injectable } from '@angular/core';
import { JWTPayload, createRemoteJWKSet, jwtVerify } from 'jose';
import {
  AUTH_CONFIG, ID_TOKEN_CLOCK_TOLERANCE_SECONDS, ID_TOKEN_SIGNING_ALGORITHMS,
} from './auth.config';
import { IdTokenRejectedError } from './errors';

export interface IdTokenChecks {
  /** From the discovery document, not from `AUTH_CONFIG` — see `issuer` below. */
  jwksUri: string;
  /**
   * The issuer the token must carry. This is the value the provider advertises in its
   * discovery document, which for this IdP is NOT the URL the app is configured with:
   * it announces `…:443/openam/oauth2` while the config says `…/openam/oauth2/realms/tudo`.
   * Comparing against the configured URL would reject every token.
   */
  issuer: string;
  /** Only for the authorization-code exchange; a refresh carries no nonce to compare. */
  nonce?: string;
  /** On refresh the subject must not change from the token being replaced. */
  expectedSubject?: string;
}

/** Refetch the key set at most this often, so a bogus `kid` cannot be used to hammer the IdP. */
const JWKS_COOLDOWN_MS = 30_000;
const JWKS_CACHE_MAX_AGE_MS = 10 * 60_000;
const JWKS_TIMEOUT_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class IdTokenVerifier {
  private keySet: ReturnType<typeof createRemoteJWKSet> | null = null;
  private keySetUri: string | null = null;

  /**
   * Resolves to the verified claims, or throws `IdTokenRejectedError`. Callers must never
   * fall back to reading an unverified token when this throws.
   */
  async verify(idToken: string, checks: IdTokenChecks): Promise<JWTPayload> {
    let payload: JWTPayload;
    try {
      ({ payload } = await jwtVerify(idToken, this.resolveKeySet(checks.jwksUri), {
        issuer: checks.issuer,
        audience: AUTH_CONFIG.clientId,
        algorithms: ID_TOKEN_SIGNING_ALGORITHMS,
        clockTolerance: ID_TOKEN_CLOCK_TOLERANCE_SECONDS,
      }));
    } catch (cause: unknown) {
      throw new IdTokenRejectedError(describe(cause));
    }

    // jose covers signature, iss, aud, exp and nbf. The rest is OIDC-specific.
    this.checkAuthorizedParty(payload);
    this.checkNonce(payload, checks.nonce);
    this.checkSubject(payload, checks.expectedSubject ?? null);
    return payload;
  }

  /**
   * One key set per URI, kept across calls: it caches the keys and handles rotation by
   * refetching when an unknown `kid` shows up.
   */
  private resolveKeySet(jwksUri: string): ReturnType<typeof createRemoteJWKSet> {
    if (!this.keySet || this.keySetUri !== jwksUri) {
      this.keySetUri = jwksUri;
      this.keySet = createRemoteJWKSet(new URL(jwksUri), {
        cooldownDuration: JWKS_COOLDOWN_MS,
        cacheMaxAge: JWKS_CACHE_MAX_AGE_MS,
        timeoutDuration: JWKS_TIMEOUT_MS,
      });
    }
    return this.keySet;
  }

  private checkAuthorizedParty(payload: JWTPayload): void {
    const azp = payload['azp'];
    if (azp !== undefined && azp !== AUTH_CONFIG.clientId) {
      throw new IdTokenRejectedError('azp does not match the client id');
    }
    if (Array.isArray(payload.aud) && payload.aud.length > 1 && azp === undefined) {
      throw new IdTokenRejectedError('several audiences but no azp');
    }
  }

  private checkNonce(payload: JWTPayload, expected: string | undefined): void {
    if (expected === undefined) return;
    // A nonce was sent, so OIDC requires the claim to be present and to match.
    if (payload['nonce'] !== expected) {
      throw new IdTokenRejectedError('nonce does not match the authorization request');
    }
  }

  private checkSubject(payload: JWTPayload, expected: string | null): void {
    if (expected !== null && payload.sub !== expected) {
      throw new IdTokenRejectedError('subject changed compared to the previous token');
    }
  }
}

/** jose attaches a `code` to everything it decides itself; a network failure has none. */
const describe = (cause: unknown): string => {
  if (typeof cause === 'object' && cause !== null && 'code' in cause) {
    return String((cause as { code: unknown }).code);
  }
  return `key set unreachable (${String(cause)})`;
};
